
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { PrintFormat as PrintFormatModel } from '@/model/PrintFormat';
import PrintPriceLevel from './PrintPriceLevel.vue';
import { uniqueId, isEqual, cloneDeep, pick, keys } from 'lodash';
import PriceInput from './PriceInput.vue';
import { requiredRule } from '@/helpers/validation/requiredRule';

const defaultPriceLevel = { from: null, price: null };

const convertValue = (value?: PrintFormatModel) => {
    if (value) {
        return {
            name: value.name,
            price: value.price,
            id: value.id,
            priceLevels: value.priceLevels.map((level) => ({ ...level }))
        };
    } else {
        return {
            name: '',
            price: null,
            priceLevels: [],
        };
    }
};

interface PriceLevel {
    id?: string;
    from: number|null;
    price: number|null
}

interface CurrentValue {
    id?: string;
    name: string;
    price: number;
    priceLevels: Array<PriceLevel>,
}

const convertCurrentValue = (value: CurrentValue) => {
    const printFormat = new PrintFormatModel(value.name, value.price ?? null, value.id)
    printFormat.priceLevels = value.priceLevels.map(priceLevel => ({
        id: priceLevel.id,
        from: priceLevel.from || null,
        price: priceLevel.price
    }));

    return printFormat;
};

let lastId = 0;

@Component({
    components: {
        PrintPriceLevel,
        PriceInput
    }
})
export default class PrintFormat extends Vue {
    @Prop({
        type: PrintFormatModel,
        required: false,
        default: null,
    })
    public value?: PrintFormatModel;
    public currentValue: CurrentValue = null as unknown as CurrentValue;
    public priceLevels: Array<PriceLevel & { _id: string }> = [];
    public nameRules = [ requiredRule, (v: string) => v.length <= 8 || 'Maks. 8 znaków' ];
    public priceRules = [ requiredRule ];
    public _id!: number;
    @Prop({
        type: Boolean,
        required: false,
        default: false,
    })
    public disabled!: boolean;
    @Prop({
        type: Boolean,
        required: true,
    })
    public isLastFormat!: boolean;

    @Watch('value', {
        immediate: true
    })
    public valueChange() {
        const newCurrentValue = convertValue(this.value);
        if (!isEqual(newCurrentValue, this.currentValue) && (!this.currentValue || this.value !== null)) {
            Vue.set(this, 'currentValue', newCurrentValue);
            this.priceLevels = this.currentValue.priceLevels.map(l => ({ ...l, _id: uniqueId() }))
        }
    }

    @Watch('currentValue', {
        deep: true,
    })
    public currentValueChange() {
        const newValue = convertCurrentValue(this.currentValue);
        const valid = this.validate();
        // Emit format if it has a name, even if validation fails (e.g., incomplete price ranges)
        // This ensures the format tag remains visible at the bottom of the form
        if (!isEqual(newValue, this.value)) {
            if (valid) {
                this.$emit('input', newValue);
            } else if (this.currentValue.name && this.currentValue.name.trim() !== '') {
                // Format has a name, so emit it even if validation fails
                // This allows the format to be displayed in tags while user is editing
                this.$emit('input', newValue);
            } else {
                // Format has no name, emit null
                this.$emit('input', null);
            }
        }
    }

    @Watch('priceLevels', {
        deep: true
    })
    public priceLevelChange() {
        const levels: PriceLevel[] = this.priceLevels.map(l => {
            const returned: any = { ...l };

            delete returned._id;

            return returned;
        });

        if (!isEqual(levels, this.currentValue.priceLevels)) {
            this.currentValue.priceLevels = levels;
        }
    }

    addPriceLevel() {
        this.priceLevels.push({ ...defaultPriceLevel, _id: uniqueId() });
    }

    validate() {
        const nameValid = (this.$refs.name as any).validate(true);
        const priceValid = (this.$refs.price as any).validate(true);
        const priceLevelValid = (this.$refs.priceLevels as any[] || []).reduce((prev, el) => el.validate() && prev, true);

        return nameValid && priceValid && priceLevelValid;
    }

    deleteLevel(index: number) {
        Vue.delete(this.priceLevels, index);
    }

    mounted() {
        this._id = lastId;
        lastId++;
    }


}
